import { useState, useMemo } from "react";
import { FaChartPie } from "react-icons/fa6";
import { HiOutlineCpuChip } from "react-icons/hi2";
import {
  HiOutlineClipboardDocumentList,
  HiOutlineCalendar,
  HiOutlineBuildingOffice,
  HiOutlineUserGroup,
  HiOutlineIdentification,
  HiOutlineGlobeAlt,
  HiOutlineUsers,
  HiOutlinePresentationChartLine,
} from "react-icons/hi2";
import { FaUsers, FaLeaf, FaIndustry, FaChartLine } from "react-icons/fa";
import { CriteriaBreakdown } from "types/assessmentTypes";
import { getDisplayPercent } from "../helper/reportHelper";
import CircularChart from "./CircularChart";
import { useTranslation } from "react-i18next";

const GROUP_ICONS = {
  financial: FaChartLine,
  paei: HiOutlinePresentationChartLine,
  infrastructure: FaIndustry,
  market: FaUsers,
  sustainability: FaLeaf,
};

interface ChartsTabProps {
  assessment: any;
}

export default function ChartsTab({ assessment }: ChartsTabProps) {
  const { t } = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

  const CRITERIA_GROUPS = {
    financial: {
      id: "financial",
      title: t("criteriaGroup.financial"),
      icon: FaChartLine,
      color: "#35D9FF",
      criteria: [1, 2, 3, 4, 5, 6, 9],
    },
    paei: {
      id: "paei",
      title: t("criteriaGroup.paei"),
      icon: HiOutlinePresentationChartLine,
      color: "#6CFF4A",
      criteria: [25, 28, 26, 27],
    },
    infrastructure: {
      id: "infrastructure",
      title: t("criteriaGroup.infrastructure"),
      icon: FaIndustry,
      color: "#B45BFF",
      criteria: [19, 20, 21, 22, 23, 24],
    },
    market: {
      id: "market",
      title: t("criteriaGroup.market"),
      icon: FaUsers,
      color: "#1EA7FF",
      criteria: [7, 8, 10, 11, 12, 13, 14, 15, 16, 17, 18],
    },
    sustainability: {
      id: "sustainability",
      title: t("criteriaGroup.sustainability"),
      icon: FaLeaf,
      color: "#FF7D2D",
      criteria: [29, 30, 31, 32, 33, 34, 35, 36],
    },
  };

  // تابع تولید رنگ‌های بسیار نزدیک به هم
  const getColorPalette = (groupKey: string, index: number, total: number) => {
    const baseColors: Record<string, string> = {
      financial: "#35D9FF",
      paei: "#6CFF4A",
      infrastructure: "#B45BFF",
      market: "#1EA7FF",
      sustainability: "#FF7D2D",
    };

    const baseColor = baseColors[groupKey] || "#FFFFFF";

    // تبدیل رنگ هگز به RGB
    const hex = baseColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // تغییرات بسیار جزئی (فقط 3-5 درصد)
    const variation = 0.03; // فقط 3% تغییر
    const factor = 1 + (index - (total - 1) / 2) * variation;

    const newR = Math.min(255, Math.max(0, Math.round(r * factor)));
    const newG = Math.min(255, Math.max(0, Math.round(g * factor)));
    const newB = Math.min(255, Math.max(0, Math.round(b * factor)));

    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  // تابع دریافت آیکون برای هر آیتم
  const getCriteriaIcon = (groupKey: string, index: number) => {
    const iconSets: Record<string, any[]> = {
      financial: [
        HiOutlineClipboardDocumentList,
        HiOutlineCalendar,
        HiOutlineBuildingOffice,
        HiOutlineUserGroup,
        HiOutlineIdentification,
        HiOutlineGlobeAlt,
        HiOutlineUsers,
      ],
      paei: [
        HiOutlineUsers,
        HiOutlineUserGroup,
        HiOutlineBuildingOffice,
        HiOutlineClipboardDocumentList,
      ],
      infrastructure: [
        HiOutlineCpuChip,
        HiOutlineBuildingOffice,
        HiOutlineUsers,
        HiOutlineUserGroup,
        HiOutlineClipboardDocumentList,
        HiOutlineCalendar,
      ],
      market: [
        HiOutlineUsers,
        HiOutlineUserGroup,
        HiOutlineBuildingOffice,
        HiOutlineClipboardDocumentList,
        HiOutlineIdentification,
        HiOutlineGlobeAlt,
        HiOutlineCalendar,
        HiOutlineUsers,
        HiOutlineUserGroup,
        HiOutlineBuildingOffice,
        HiOutlineClipboardDocumentList,
      ],
      sustainability: [
        HiOutlineGlobeAlt,
        HiOutlineCalendar,
        HiOutlineUsers,
        HiOutlineBuildingOffice,
        HiOutlineClipboardDocumentList,
        HiOutlineIdentification,
        HiOutlineUserGroup,
        HiOutlineUsers,
      ],
    };

    const icons = iconSets[groupKey] || [
      HiOutlineClipboardDocumentList,
      HiOutlineUsers,
      HiOutlineBuildingOffice,
      HiOutlineUserGroup,
    ];
    return icons[index % icons.length];
  };

  const groupScores = useMemo(() => {
    if (!assessment?.scoringResults?.[0]?.criteriaBreakdown) {
      return null;
    }

    const criteriaBreakdown = assessment.scoringResults[0].criteriaBreakdown;

    const existingGroupAverages: { groupKey: string; avg: number }[] = [];

    const groups = Object.entries(CRITERIA_GROUPS).map(([key, group]) => {
      const scores: number[] = [];

      group.criteria.forEach((index) => {
        const found = criteriaBreakdown.find(
          (c: CriteriaBreakdown) => c.criteriaIndex === index
        );
        if (found) {
          const score = getDisplayPercent(found);
          if (score !== undefined && score !== null && !isNaN(score)) {
            scores.push(score);
          }
        }
      });

      const avg =
        scores.length > 0
          ? scores.reduce((sum, s) => sum + s, 0) / scores.length
          : 0;

      if (scores.length > 0) {
        existingGroupAverages.push({ groupKey: key, avg });
      }

      return {
        ...group,
        key,
        score: Math.round(avg * 100) / 100,
        scores,
        existingCount: scores.length,
        totalCount: group.criteria.length,
        hasData: scores.length > 0,
      };
    });

    const totalAvg =
      existingGroupAverages.length > 0
        ? Math.round(
            (existingGroupAverages.reduce((sum, item) => sum + item.avg, 0) /
              existingGroupAverages.length) *
              100
          ) / 100
        : 0;

    return {
      groups,
      totalAvg,
      existingGroupsCount: existingGroupAverages.length,
      totalGroupsCount: Object.keys(CRITERIA_GROUPS).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessment]);

  const chartData = useMemo(() => {
    if (!groupScores) return [];

    return groupScores.groups
      .filter((group) => group.hasData)
      .map((group) => ({
        id: group.key,
        title: group.title,
        value: group.score,
        color: group.color,
        icon: GROUP_ICONS[group.key as keyof typeof GROUP_ICONS] || group.icon,
      }));
  }, [groupScores]);

  const selectedGroupData = useMemo(() => {
    if (!selectedGroup || !assessment?.scoringResults?.[0]?.criteriaBreakdown) {
      return null;
    }

    const group = Object.values(CRITERIA_GROUPS).find(
      (g) => g.id === selectedGroup
    );
    if (!group) return null;

    const criteriaBreakdown = assessment.scoringResults[0].criteriaBreakdown;

    const data = group.criteria
      .map((index) => {
        const found = criteriaBreakdown.find(
          (c: CriteriaBreakdown) => c.criteriaIndex === index
        );
        if (!found) return null;

        const score = getDisplayPercent(found);
        return {
          criteriaIndex: index,
          label: t(`criteria.${index}`),
          score: Math.round(score * 100) / 100,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    const groupAvg =
      data.length > 0
        ? Math.round(
            (data.reduce((sum, item) => sum + item.score, 0) / data.length) *
              100
          ) / 100
        : 0;

    return {
      id: group.id,
      title: group.title,
      color: group.color,
      icon: GROUP_ICONS[group.id as keyof typeof GROUP_ICONS] || group.icon,
      data: data,
      average: groupAvg,
      existingCount: data.length,
      totalCount: group.criteria.length,
      hasData: data.length > 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, assessment]);

  const handleSegmentClick = (item: any) => {
    setSelectedGroup(item.id);
  };

  if (!groupScores || chartData.length === 0) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center text-center">
        <HiOutlineCpuChip className="mb-4 h-16 w-16 text-white/70" />
        <p className="text-lg font-light text-white/80">
          {t("assessment.report.noData")}
        </p>
        <p className="text-sm text-white/70">
          {t("assessment.report.noDataHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white text-center dark:bg-navy-800">
      <div className="flex flex-wrap items-start gap-8 overflow-hidden rounded-3xl border border-gray-100 bg-navy-800 p-6 dark:border-navy-700 dark:bg-navy-800">
        {/* Main Chart */}
        <div className="min-w-[250px] flex-1">
          <h3 className="text-center text-lg font-semibold text-white dark:text-white">
            {t("assessment.report.circularChart")}
          </h3>
          <CircularChart
            data={chartData}
            centerTitle={groupScores?.totalAvg?.toString() || "0"}
            centerSubtitle={t("assessment.report.average")}
            centerIcon={FaChartPie}
            centerBorderColor="#FFD700"
            onSegmentClick={handleSegmentClick}
            showPercentage={true}
            showValue={false}
            totalValue={100}
          />
        </div>

        {/* Detail Chart */}
        <div className="min-w-[250px] flex-1">
          {selectedGroupData &&
          selectedGroupData.hasData &&
          selectedGroupData.data.length > 0 ? (
            <>
              <h3 className="text-center text-lg font-semibold text-white dark:text-white">
                <span className="flex items-center justify-center gap-2">
                  <selectedGroupData.icon
                    className="h-5 w-5"
                    style={{ color: selectedGroupData.color }}
                  />
                  {selectedGroupData.title}
                </span>
              </h3>

              <CircularChart
                data={selectedGroupData.data.map((item, index) => {
                  const total = selectedGroupData.data.length;
                  const Icon = getCriteriaIcon(selectedGroupData.id, index);
                  const color = getColorPalette(
                    selectedGroupData.id,
                    index,
                    total
                  );

                  return {
                    id: item.criteriaIndex,
                    title: item.label,
                    fullTitle: item.label,
                    value: item.score,
                    color: color,
                    icon: Icon,
                  };
                })}
                centerTitle={selectedGroupData.average.toFixed(1)}
                centerSubtitle={t("assessment.report.average")}
                centerIcon={selectedGroupData.icon}
                centerBorderColor={selectedGroupData.color}
                showPercentage={true}
                showValue={false}
                className="scale-90"
                totalValue={100}
              />
            </>
          ) : (
            <div className="flex h-[400px] flex-col items-center justify-center text-center">
              <HiOutlineCpuChip className="mb-4 h-16 w-16 text-white/70" />
              <p className="text-lg font-light text-white/80">
                {t("assessment.report.selectGroup")}
              </p>
              <p className="text-sm text-white/70">
                {t("assessment.report.selectGroupHint")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
